import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import { Link } from 'react-router-dom';
import { graphql } from 'react-apollo';

import LogoutLink from '../../../../../components/auth/LogoutLink';
import withBasename from '../../../../../components/routing/withBasename';
import Angle from '../../../../../components/global/Angle';
import { IMAGE_SIZE_CODE } from '../../../../../constants/image-size-codes';
import { getProductBasenames } from '../../../../../util/role-definitions';
import { getThumbBySize } from '../../../../../util/photo';
import { getAsset } from '../../../../../util/assets';
import { MeQuery } from '../../../../../graphql/queries/account';
import DropdownMenu from '../../../../../components/global/DropdownMenu';
import FeatureBlock from '../../../../../components/contextual/FeatureBlock';
import {
  canAccessAccountBrandingOnBasename,
  canAccessAccountCreationsOnBasename,
  canAccessAccountMatchesOnBasename,
  canAccessAccountPricingOnBasename,
  canAccessAccountReferralsOnBasename,
  canAccessMemberConnectOnBasename,
} from '../../../../../util/product-permissions';
import AssistanceActionButton from '../../../components/AssistanceActionButton';
import MegaphoneIcon from '../../../../../components/icons/MegaphoneIcon';
import SupportIcon from '../../../../../components/icons/SupportIcon';
import SignOutIcon from '../../../../../components/icons/SignOutIcon';
import BookmarkIcon from '../../../../../components/icons/BookmarkIcon';
import PriceTagIcon from '../../../../../components/icons/PriceTagIcon';
import { isEligibleForShutterbugFreeTrial } from '../../../../temp/views/onboarding/PhotoSearchCheckout/helpers';
import { isEligibleForPhotoManagerFreeTrial } from '../../Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/helpers';
import { BASENAME } from '../../../../../constants/basenames';
import MyProfileIcon from '../../../../../components/icons/MyProfileIcon';
import SettingsIcon from '../../../../../components/icons/SettingsIcon';
import DownloadIcon from '../../../../../components/icons/DownloadIcon';
import { isProAccount } from '../../../../../util/account';
import RevenueIcon from '../../../../../components/icons/RevenueIcon';
import DraftWaldogramsIcon from '../../../../../components/icons/DraftWaldogramsIcon';
import MyMatchesIcon from '../../../../../components/icons/MyMatchesIcon';
import ShopIcon from '../../../../../components/icons/ShopIcon';
import SearchIcon from '../../../../../components/icons/SearchIcon';
import Button from '../../../../../components/global/Button';
import useActionableItemMenu from '../UserActionableItemMenu/useActionableItemMenu';
import NotificationCounter from '../../../components/NotificationCounter';
import CirclesIcon from '../../../../../components/icons/CirclesIcon';
import StatusCounter from '../../../components/NotificationCounter/components/StatusCounter';

const propTypes = {
  basename: PropTypes.oneOf(getProductBasenames()).isRequired,
  className: PropTypes.string,
  avatarPhoto: PropTypes.shape({
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        size_code: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }),
    ),
  }),
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
};

const UserAccountMenu = ({ basename, className, avatarPhoto, account }) => {
  const {
    onNotificationsSidebarToggle,
    actionableItems,
  } = useActionableItemMenu();

  const smallThumbnail =
    getThumbBySize(avatarPhoto?.thumbnail_urls, IMAGE_SIZE_CODE.SMALL)?.url ??
    getAsset('/user-menu-default.png');

  const classes = classNames({
    'user-account-menu': true,
    'navbar-right-button': true,
    [className]: !!className,
  });

  const isConsumer = basename === BASENAME.SHUTTERBUG;

  const canRequestFreeTrial = isConsumer
    ? isEligibleForShutterbugFreeTrial(account)
    : isEligibleForPhotoManagerFreeTrial(account);

  const subscriptionsUrl = isConsumer ? 'subscriptions' : 'photo-manager';

  const actionableItemCount = actionableItems?.total ?? 0;
  const hasActionableItems = actionableItemCount > 0;

  return (
    <div className={classes}>
      <DropdownMenu
        placement="bottom-end"
        toggle={
          <Fragment>
            <div
              id="user-account-menu-selfie-circle-btn"
              className="user-account-menu-selfie-circle-button"
              style={{ backgroundImage: `url("${smallThumbnail}")` }}
            >
              {hasActionableItems && (
                <StatusCounter
                  className="notification-counter-count"
                  count={actionableItemCount}
                />
              )}
            </div>
            <Angle direction="down" />
          </Fragment>
        }
        toggleClassName="user-account-menu-toggle-button"
        closeOnClickWithin
      >
        {hasActionableItems && (
          <Button
            btnStyle="reset"
            className="dropdown-item"
            onClick={onNotificationsSidebarToggle}
          >
            <NotificationCounter
              className="mr-2"
              size={20}
              count={actionableItemCount}
            />{' '}
            Notifications
          </Button>
        )}
        <FeatureBlock condition={canRequestFreeTrial}>
          <Link
            // TODO as per the designs this needs to say diff things
            // for different states i.e. the user is subscribed or not etc
            className="dropdown-item user-account-menu-action-item"
            to={`/app/${basename}/account/settings/${subscriptionsUrl}`}
          >
            <img
              src={getAsset('/diamond-orange.svg')}
              className="upgrade-freemium-icon"
              alt="Upgrade subscription"
            />{' '}
            Upgrade subscription
          </Link>
          <div className="dropdown-item-separator" />
        </FeatureBlock>
        <FeatureBlock condition={canAccessAccountMatchesOnBasename(basename)}>
          <Link
            className="dropdown-item navbar-item-mobile-only"
            to={`/app/${basename}/identities`}
          >
            <MyMatchesIcon
              size={20}
              className="mr-2"
              color="#50a6ff"
              id="account-dropdown-matches-icon"
            />{' '}
            My people
          </Link>
          <Link
            className="dropdown-item"
            to={`/app/${basename}/account/circles`}
          >
            <CirclesIcon size={20} className="mr-2" color="#50a6ff" /> My
            circles
          </Link>
          {account?.entitled_photos?.total > 0 && (
            <Link
              className="dropdown-item navbar-item-mobile-only"
              to={`/app/${basename}/shop`}
            >
              <ShopIcon size={20} className="mr-2" color="#50a6ff" /> Store
            </Link>
          )}
          <Link className="dropdown-item" to={`/app/${basename}/search`}>
            <SearchIcon size={20} className="mr-2" color="#50a6ff" /> Have a
            join code?
          </Link>
          <div className="dropdown-item-separator" />
        </FeatureBlock>
        <FeatureBlock condition={canAccessMemberConnectOnBasename(basename)}>
          <Link
            className="dropdown-item navbar-item-mobile-only"
            to={`/app/${basename}/member-connect`}
          >
            <CirclesIcon size={20} className="mr-2" color="#50a6ff" /> Member
            Connect
          </Link>
          <div className="dropdown-item-separator" />
        </FeatureBlock>

        <div className="dropdown-item dropdown-item-section-title">
          My Account
        </div>

        <Link
          className="dropdown-item"
          id="user-account-menu-settings-btn"
          to={`/app/${basename}/account/settings/details`}
        >
          <SettingsIcon color="#50a6ff" size={20} className="mr-2" /> Account
          Settings
        </Link>

        {account?.username && isProAccount(account) && (
          <Link
            className="dropdown-item"
            to={`/app/${basename}/profile/@${account.username}`}
          >
            <MyProfileIcon color="#50a6ff" size={20} className="mr-2" /> View
            Profile Page
          </Link>
        )}
        <FeatureBlock condition={canAccessAccountPricingOnBasename(basename)}>
          <Link
            className="dropdown-item"
            to={`/app/${basename}/account/revenue`}
          >
            <RevenueIcon color="#50a6ff" size={20} className="mr-2" /> Revenue
          </Link>
        </FeatureBlock>
        <FeatureBlock condition={canAccessAccountPricingOnBasename(basename)}>
          <Link
            className="dropdown-item"
            to={`/app/${basename}/account/settings/pricing`}
          >
            <PriceTagIcon color="#50a6ff" size={20} className="mr-2" /> My
            Products
          </Link>
        </FeatureBlock>

        <FeatureBlock condition={canAccessAccountBrandingOnBasename(basename)}>
          <Link
            className="dropdown-item"
            to={`/app/${basename}/account/settings/branding`}
          >
            <BookmarkIcon color="#50a6ff" size={20} className="mr-2" /> My
            Branding
          </Link>
        </FeatureBlock>

        <FeatureBlock condition={canAccessAccountReferralsOnBasename(basename)}>
          <Link
            className="dropdown-item"
            to={`/app/${basename}/account/settings/referrals`}
          >
            <MegaphoneIcon color="#50a6ff" size={20} className="mr-2" /> Refer a
            Friend
          </Link>
        </FeatureBlock>
        <FeatureBlock condition={canAccessAccountCreationsOnBasename(basename)}>
          <Link
            className="dropdown-item"
            to={`/app/${basename}/account/cards/list`}
          >
            <DraftWaldogramsIcon color="#50a6ff" size={20} className="mr-2" />{' '}
            My Creations
          </Link>
        </FeatureBlock>
        {account?.photo_downloads?.total > 0 && (
          <Link
            className="dropdown-item"
            to={`/app/${basename}/account/settings/downloads`}
          >
            <DownloadIcon color="#50a6ff" size={20} className="mr-2" /> Recent
            Downloads
          </Link>
        )}
        <div className="dropdown-item-separator" />

        <AssistanceActionButton
          className="dropdown-item"
          icon={<SupportIcon color="white" size={20} className="mr-2" />}
        />
        <LogoutLink
          className="dropdown-item text-danger"
          icon={<SignOutIcon color="#fe5851" size={20} className="mr-2" />}
        />
      </DropdownMenu>
    </div>
  );
};

UserAccountMenu.propTypes = propTypes;

const withPhoto = graphql(MeQuery, {
  props: ({ data }) => ({
    avatarPhoto: data?.me?.avatar_photo,
  }),
});

export { UserAccountMenu as UserAccountMenuComponent };

export default compose(withBasename, withPhoto)(UserAccountMenu);
